import { LockShieldBold, LockShieldLight, CalendarCheckLight } from '@ha/icons';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useIntl } from 'ha/i18n';

import { Card, CardContent, Grid, Link, Typography, units } from '@hbf/dsl';
import { ListingCancellationPolicy } from '@ha/api/v2/types/Listing';

export type Props = {
  testLocator?: string;
  cancellationPolicy: ListingCancellationPolicy;
  displayInRows?: boolean;
  withFooter?: boolean;
};

const useStyles = makeStyles()(theme => ({
  subtitles: {
    marginBottom: theme.spacing(1),
  },
  titles: {
    marginBottom: theme.spacing(1),
    fontWeight: theme.typography.fontWeightRegular,
  },
  sameHeight: {
    // This allows all grid items to have the same height even with different content
    display: 'flex',
  },
  sameWidth: {
    // This allows all cards to have the same width
    width: '100%',
  },
  descriptionContainer: {
    padding: theme.spacing(2, 2, 1, 2),
    width: '100%',
  },
  description: {
    height: '100%',
  },
  icon: {
    verticalAlign: 'text-top',
    marginRight: theme.spacing(1),
  },
  list: {
    listStyleType: 'circle',
    color: theme.palette.secondary.dark,
    paddingLeft: theme.spacing(2.4),
    marginTop: theme.spacing(1),
  },
  listItem: {
    color: theme.palette.grey[700],
    lineHeight: '2',
  },
  footer: {
    color: theme.palette.grey[500],
    fontSize: units.rem(0.85),
  },
}));

const CancellationPolicyOld: React.FC<Props> = ({
  testLocator,
  cancellationPolicy,
  displayInRows = false,
}) => {
  const { classes } = useStyles();
  const { T, urlResolver } = useIntl();

  const content = {
    [ListingCancellationPolicy.STRICT]: {
      first: {
        subtitle: T('cancellationPolicy.fixed.first.subtitle'),
        percentage: T('cancellationPolicy.fixed.first.percentage'),
        title: T('cancellationPolicy.fixed.first.title'),
        text: T('cancellationPolicy.fixed.first.text'),
      },
      second: {
        subtitle: T('cancellationPolicy.fixed.second.subtitle'),
        percentage: T('cancellationPolicy.fixed.second.percentage'),
        title: T('cancellationPolicy.fixed.second.title'),
        text: T('cancellationPolicy.fixed.second.text'),
      },
      third: {
        title: T('cancellationPolicy.fixed.third.title'),
        link: T('Read More'),
        text: T('cancellationPolicy.fixed.third.text'),
      },
    },
    [ListingCancellationPolicy.FLEXIBLE]: {
      first: {
        subtitle: T('cancellationPolicy.flexible.first.subtitle'),
        percentage: T('cancellationPolicy.flexible.first.percentage'),
        title: T('cancellationPolicy.flexible.first.title'),
        text: T('cancellationPolicy.flexible.first.text'),
      },
      second: {
        subtitle: T('cancellationPolicy.flexible.second.subtitle'),
        percentage: T('cancellationPolicy.flexible.second.percentage'),
        title: T('cancellationPolicy.flexible.second.title'),
        text: T('cancellationPolicy.flexible.second.text'),
      },
      third: {
        title: T('cancellationPolicy.flexible.third.title'),
        link: T('Read More'),
        text: T('cancellationPolicy.flexible.third.text'),
      },
    },
  };

  return (
    <Grid
      container
      spacing={2}
      data-test-locator={testLocator}
      alignItems="stretch"
      justifyContent="space-between"
    >
      <Grid
        item
        xs={12}
        md={displayInRows ? 12 : 6}
        className={classes.sameHeight}
      >
        <Card variant="outlined" className={classes.sameWidth}>
          <CardContent>
            <Typography.H4 className={classes.subtitles}>
              {content[cancellationPolicy].first.subtitle}
            </Typography.H4>
            <Typography.H3 color="mint" className={classes.titles}>
              <strong>{content[cancellationPolicy].first.percentage}</strong>
              {` ${content[cancellationPolicy].first.title}`}
            </Typography.H3>
            <Typography.Paragraph>
              {content[cancellationPolicy].first.text}
            </Typography.Paragraph>
          </CardContent>
        </Card>
      </Grid>
      <Grid
        item
        xs={12}
        md={displayInRows ? 12 : 6}
        className={classes.sameHeight}
      >
        <Card variant="outlined" className={classes.sameWidth}>
          <CardContent>
            <Typography.H4 className={classes.subtitles}>
              {content[cancellationPolicy].second.subtitle}
            </Typography.H4>
            <Typography.H3 className={classes.titles}>
              <strong>{content[cancellationPolicy].second.percentage}</strong>
              {` ${content[cancellationPolicy].second.title}`}
            </Typography.H3>
            <Typography.Paragraph>
              {content[cancellationPolicy].second.text}
            </Typography.Paragraph>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} className={classes.sameHeight}>
        <Card variant="outlined" className={classes.sameWidth}>
          <CardContent>
            <Typography.H4 className={classes.subtitles}>
              <LockShieldBold
                fontSize="small"
                color="secondary"
                className={classes.icon}
              />
              {content[cancellationPolicy].third.title}
            </Typography.H4>
            <Typography.Paragraph>
              {content[cancellationPolicy].third.text}
            </Typography.Paragraph>
            <Typography.Paragraph>
              <Link
                to={urlResolver.getCancellationPolicyHelpUrl()}
                target="_blank"
                kind="mint"
              >
                {content[cancellationPolicy].third.link}
              </Link>
            </Typography.Paragraph>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const CancellationPolicyNext: React.FC<Props> = ({
  testLocator,
  cancellationPolicy,
  withFooter = true,
}) => {
  const { classes } = useStyles();
  const { T, urlResolver } = useIntl();

  return (
    <Grid
      container
      spacing={2}
      data-test-locator={testLocator}
      alignItems="stretch"
      justifyContent="space-between"
    >
      {cancellationPolicy === ListingCancellationPolicy.STRICT && (
        <Grid item xs={12} md={8} className={classes.sameHeight}>
          <Card variant="outlined" className={classes.descriptionContainer}>
            <Grid
              container
              direction="column"
              justifyContent="space-between"
              className={classes.description}
            >
              <Grid item>
                <Typography.H4 className={classes.subtitles}>
                  <CalendarCheckLight
                    fontSize="small"
                    color="secondary"
                    className={classes.icon}
                  />
                  {T('listings.details.cancellation_policy.strict.name')}
                </Typography.H4>
                <Typography.Paragraph>
                  {T(
                    'listings.details.cancellation_policy.component.strict.description_1',
                  )}
                </Typography.Paragraph>
                <ul className={classes.list}>
                  <li>
                    <span className={classes.listItem}>
                      {T(
                        'listings.details.cancellation_policy.component.strict.description_2',
                      )}
                    </span>
                  </li>
                  <li>
                    <span className={classes.listItem}>
                      {T(
                        'listings.details.cancellation_policy.component.strict.description_3',
                      )}
                    </span>
                  </li>
                </ul>
              </Grid>
              {withFooter && (
                <Grid item>
                  <Typography.Paragraph className={classes.footer}>
                    {T('listings.details.cancellation_policy.footer')}
                  </Typography.Paragraph>
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>
      )}

      {cancellationPolicy === ListingCancellationPolicy.FLEXIBLE_30D && (
        <Grid item xs={12} md={8} className={classes.sameHeight}>
          <Card variant="outlined" className={classes.descriptionContainer}>
            <Grid
              container
              direction="column"
              justifyContent="space-between"
              className={classes.description}
            >
              <Grid item>
                <Typography.H4 className={classes.subtitles}>
                  <CalendarCheckLight
                    fontSize="small"
                    color="secondary"
                    className={classes.icon}
                  />
                  {T(
                    'listings.details.cancellation_policy.flexible_thirty.name',
                  )}
                </Typography.H4>
                <Typography.Paragraph>
                  {T(
                    'listings.details.cancellation_policy.component.flexible_thirty.description_1',
                  )}
                </Typography.Paragraph>
                <Typography.Paragraph>
                  {T(
                    'listings.details.cancellation_policy.component.flexible_thirty.description_2',
                  )}
                </Typography.Paragraph>
                <ul className={classes.list}>
                  <li>
                    <span className={classes.listItem}>
                      {T(
                        'listings.details.cancellation_policy.component.flexible_thirty.description_3',
                      )}
                    </span>
                  </li>
                  <li>
                    <span className={classes.listItem}>
                      {T(
                        'listings.details.cancellation_policy.component.flexible_thirty.description_4',
                      )}
                    </span>
                  </li>
                  <li>
                    <span className={classes.listItem}>
                      {T(
                        'listings.details.cancellation_policy.component.flexible_thirty.description_5',
                      )}
                    </span>
                  </li>
                </ul>
              </Grid>
              {withFooter && (
                <Grid item>
                  <Typography.Paragraph className={classes.footer}>
                    {T('listings.details.cancellation_policy.footer')}
                  </Typography.Paragraph>
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>
      )}

      <Grid item xs={12} md={4} className={classes.sameHeight}>
        <Card variant="outlined" className={classes.sameWidth}>
          <CardContent>
            <Typography.H4 className={classes.subtitles}>
              <LockShieldLight
                fontSize="small"
                color="secondary"
                className={classes.icon}
              />
              {T('listings.details.cancellation_policy.48h_safeguard.name')}
            </Typography.H4>
            <Typography.Paragraph>
              {T('listings.details.cancellation_policy.48h_safeguard')}
              {` `}
              <Link
                to={urlResolver.getCancellationPolicyHelpUrl()}
                target="_blank"
                kind="mint"
              >
                {T('cta_learn_more')}
              </Link>
            </Typography.Paragraph>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const CancellationPolicy: React.FC<Props> = ({
  testLocator,
  cancellationPolicy,
  displayInRows = false,
  withFooter = true,
}) =>
  // If the policy type is flexible show the old interface
  cancellationPolicy !== ListingCancellationPolicy.FLEXIBLE ? (
    <CancellationPolicyNext
      cancellationPolicy={cancellationPolicy}
      testLocator={testLocator}
      withFooter={withFooter}
    />
  ) : (
    <CancellationPolicyOld
      cancellationPolicy={cancellationPolicy}
      displayInRows={displayInRows}
      testLocator={testLocator}
    />
  );
export { CancellationPolicy };
